import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getBranches, createBranch, updateBranch, deleteBranch } from '../../services/api';
import api from '../../services/api';
import {
  Plus, MapPin, Edit, Trash2, X, Clock, Truck, Store,
  Power, PowerOff, Settings, ChevronDown, ChevronUp, Save, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import LocationPicker from '../../components/dashboard/LocationPicker';

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' }
];

const DEFAULT_HOURS = {
  monday: { is_open: true, open_time: '09:00', close_time: '18:00' },
  tuesday: { is_open: true, open_time: '09:00', close_time: '18:00' },
  wednesday: { is_open: true, open_time: '09:00', close_time: '18:00' },
  thursday: { is_open: true, open_time: '09:00', close_time: '18:00' },
  friday: { is_open: true, open_time: '09:00', close_time: '18:00' },
  saturday: { is_open: true, open_time: '09:00', close_time: '14:00' },
  sunday: { is_open: false, open_time: null, close_time: null }
};

const Sucursales = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [activeTab, setActiveTab] = useState('info'); // info, hours, delivery
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    lat: '',
    lng: '',
    // New fields
    pickup_enabled: true,
    delivery_enabled: false,
    delivery_radius_km: 5,
    delivery_fee: 0,
    min_order_amount: 0,
    business_hours: DEFAULT_HOURS,
    delivery_zones: [],
    hide_products_from_marketplace: false
  });

  useEffect(() => {
    loadBranches();
  }, [user]);

  const loadBranches = async () => {
    if (!user?.business_id) return;

    try {
      const response = await getBranches(user.business_id);
      setBranches(response.data);
    } catch (error) {
      console.error('Error loading branches:', error);
      toast.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const branchData = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        business_id: user.business_id,
        location: formData.lat && formData.lng ? {
          lat: parseFloat(formData.lat),
          lng: parseFloat(formData.lng),
        } : null,
        pickup_enabled: formData.pickup_enabled,
        delivery_enabled: formData.delivery_enabled,
        delivery_radius_km: parseFloat(formData.delivery_radius_km) || 5,
        delivery_fee: parseFloat(formData.delivery_fee) || 0,
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        business_hours: formData.business_hours,
        delivery_zones: formData.delivery_zones.length > 0 ? formData.delivery_zones : null,
        hide_products_from_marketplace: formData.hide_products_from_marketplace
      };

      if (editingBranch) {
        await updateBranch(editingBranch.id, branchData);
        toast.success('Sucursal actualizada');
      } else {
        await createBranch(branchData);
        toast.success('Sucursal creada');
      }

      setShowModal(false);
      setEditingBranch(null);
      resetForm();
      loadBranches();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error(getErrorMessage(error, 'Error al guardar sucursal'));
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      address: branch.address,
      phone: branch.phone || '',
      lat: branch.location?.lat || '',
      lng: branch.location?.lng || '',
      pickup_enabled: branch.pickup_enabled ?? true,
      delivery_enabled: branch.delivery_enabled ?? false,
      delivery_radius_km: branch.delivery_radius_km || 5,
      delivery_fee: branch.delivery_fee || 0,
      min_order_amount: branch.min_order_amount || 0,
      business_hours: branch.business_hours || DEFAULT_HOURS,
      delivery_zones: branch.delivery_zones || [],
      hide_products_from_marketplace: branch.hide_products_from_marketplace || false
    });
    setActiveTab('info');
    setShowModal(true);
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sucursal?')) return;

    try {
      await deleteBranch(branchId);
      toast.success('Sucursal eliminada');
      loadBranches();
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Error al eliminar sucursal');
    }
  };

  const toggleBranchStatus = async (branch) => {
    try {
      await api.post(`/branches/${branch.id}/toggle-status`);
      toast.success(branch.is_manually_closed ? 'Sucursal abierta' : 'Sucursal cerrada temporalmente');
      loadBranches();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      lat: '',
      lng: '',
      pickup_enabled: true,
      delivery_enabled: false,
      delivery_radius_km: 5,
      delivery_fee: 0,
      min_order_amount: 0,
      business_hours: DEFAULT_HOURS,
      delivery_zones: [],
      hide_products_from_marketplace: false
    });
    setActiveTab('info');
  };

  const updateDaySchedule = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  const addDeliveryZone = () => {
    setFormData(prev => ({
      ...prev,
      delivery_zones: [
        ...prev.delivery_zones,
        { min_km: prev.delivery_zones.length > 0 ? prev.delivery_zones[prev.delivery_zones.length - 1].max_km : 0, max_km: 5, fee: 0 }
      ]
    }));
  };

  const updateDeliveryZone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      delivery_zones: prev.delivery_zones.map((zone, i) => 
        i === index ? { ...zone, [field]: parseFloat(value) || 0 } : zone
      )
    }));
  };

  const removeDeliveryZone = (index) => {
    setFormData(prev => ({
      ...prev,
      delivery_zones: prev.delivery_zones.filter((_, i) => i !== index)
    }));
  };

  const formatHours = (hours) => {
    if (!hours) return 'Sin configurar';
    const openDays = DAYS.filter(d => hours[d.key]?.is_open);
    if (openDays.length === 0) return 'Cerrado todos los días';
    if (openDays.length === 7) return 'Abierto todos los días';
    return `${openDays.length} días abierto`;
  };

  const getBranchStatusBadge = (branch) => {
    if (branch.is_manually_closed) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">Cerrado manualmente</span>;
    }
    if (!branch.is_active) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">Inactiva</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Activa</span>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Sucursales</h1>
            <p className="text-slate-600 mt-2">Gestiona ubicaciones, horarios y zonas de entrega</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingBranch(null);
              setShowModal(true);
            }}
            data-testid="add-branch-button"
            className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva Sucursal
          </button>
        </div>

        {/* Branches Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading ? (
            <div className="col-span-full flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
            </div>
          ) : branches.length === 0 ? (
            <div className="col-span-full bg-white rounded-xl border border-slate-200 p-12 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No hay sucursales registradas</p>
            </div>
          ) : (
            branches.map((branch) => (
              <div
                key={branch.id}
                data-testid={`branch-card-${branch.id}`}
                className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
              >
                {/* Header */}
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${branch.is_manually_closed ? 'bg-red-50' : 'bg-teal-50'}`}>
                        <MapPin className={`w-6 h-6 ${branch.is_manually_closed ? 'text-red-600' : 'text-teal-700'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{branch.name}</h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          {getBranchStatusBadge(branch)}
                          {branch.hide_products_from_marketplace && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium flex items-center gap-1">
                              <EyeOff className="w-3 h-3" /> Oculto en marketplace
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleBranchStatus(branch)}
                      className={`p-2 rounded-lg transition-colors ${
                        branch.is_manually_closed 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                      title={branch.is_manually_closed ? 'Abrir sucursal' : 'Cerrar sucursal'}
                    >
                      {branch.is_manually_closed ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                    <span>{branch.address}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>{formatHours(branch.business_hours)}</span>
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    {branch.pickup_enabled && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Store className="w-4 h-4" /> Recolección
                      </span>
                    )}
                    {branch.delivery_enabled && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Truck className="w-4 h-4" /> Entrega ({branch.delivery_radius_km} km)
                      </span>
                    )}
                  </div>

                  {branch.delivery_enabled && branch.delivery_fee > 0 && (
                    <div className="text-sm text-slate-500">
                      Costo de envío: ${branch.delivery_fee.toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 p-4 pt-0">
                  <button
                    onClick={() => handleEdit(branch)}
                    data-testid={`edit-branch-${branch.id}`}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Configurar
                  </button>
                  <button
                    onClick={() => handleDelete(branch.id)}
                    data-testid={`delete-branch-${branch.id}`}
                    className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingBranch ? 'Configurar Sucursal' : 'Nueva Sucursal'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingBranch(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200 px-6">
              {[
                { id: 'info', label: 'Información', icon: MapPin },
                { id: 'hours', label: 'Horarios', icon: Clock },
                { id: 'delivery', label: 'Entrega', icon: Truck }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id 
                      ? 'border-teal-700 text-teal-700' 
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {/* Info Tab */}
                {activeTab === 'info' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Nombre de la Sucursal *</label>
                      <input
                        type="text"
                        required
                        data-testid="branch-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Ej: Sucursal Centro"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Dirección *</label>
                      <textarea
                        required
                        rows="2"
                        data-testid="branch-address-input"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Calle, número, colonia, ciudad"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        data-testid="branch-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    {/* Location Picker with Map */}
                    <LocationPicker
                      lat={formData.lat}
                      lng={formData.lng}
                      address={formData.address}
                      onLocationChange={(lat, lng) => setFormData({ 
                        ...formData, 
                        lat: lat.toFixed(6), 
                        lng: lng.toFixed(6) 
                      })}
                    />
                  </>
                )}

                {/* Hours Tab */}
                {activeTab === 'hours' && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-500 mb-4">
                      Configura el horario de atención para cada día de la semana.
                    </p>
                    {DAYS.map(day => (
                      <div key={day.key} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <label className="flex items-center gap-2 min-w-[120px]">
                          <input
                            type="checkbox"
                            checked={formData.business_hours[day.key]?.is_open ?? true}
                            onChange={(e) => updateDaySchedule(day.key, 'is_open', e.target.checked)}
                            className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                          />
                          <span className="font-medium text-slate-700">{day.label}</span>
                        </label>
                        
                        {formData.business_hours[day.key]?.is_open && (
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              value={formData.business_hours[day.key]?.open_time || '09:00'}
                              onChange={(e) => updateDaySchedule(day.key, 'open_time', e.target.value)}
                              className="px-2 py-1 border border-slate-200 rounded text-sm"
                            />
                            <span className="text-slate-400">a</span>
                            <input
                              type="time"
                              value={formData.business_hours[day.key]?.close_time || '18:00'}
                              onChange={(e) => updateDaySchedule(day.key, 'close_time', e.target.value)}
                              className="px-2 py-1 border border-slate-200 rounded text-sm"
                            />
                          </div>
                        )}
                        
                        {!formData.business_hours[day.key]?.is_open && (
                          <span className="text-sm text-red-500">Cerrado</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Delivery Tab */}
                {activeTab === 'delivery' && (
                  <div className="space-y-6">
                    {/* Service Types */}
                    <div>
                      <h3 className="font-medium text-slate-900 mb-3">Tipos de servicio</h3>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.pickup_enabled}
                            onChange={(e) => setFormData({ ...formData, pickup_enabled: e.target.checked })}
                            className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                          />
                          <Store className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="font-medium text-slate-700">Recolección en tienda</span>
                            <p className="text-sm text-slate-500">El cliente recoge el pedido en la sucursal</p>
                          </div>
                        </label>
                        
                        <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.delivery_enabled}
                            onChange={(e) => setFormData({ ...formData, delivery_enabled: e.target.checked })}
                            className="w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                          />
                          <Truck className="w-5 h-5 text-green-600" />
                          <div>
                            <span className="font-medium text-slate-700">Entrega a domicilio</span>
                            <p className="text-sm text-slate-500">Envías el pedido a la ubicación del cliente</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Marketplace Visibility */}
                    <div className="border-t border-slate-200 pt-6">
                      <h3 className="font-medium text-slate-900 mb-3">Visibilidad en Marketplace</h3>
                      <label className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.hide_products_from_marketplace}
                          onChange={(e) => setFormData({ ...formData, hide_products_from_marketplace: e.target.checked })}
                          className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                        />
                        <EyeOff className="w-5 h-5 text-orange-600" />
                        <div>
                          <span className="font-medium text-slate-700">Ocultar productos del marketplace</span>
                          <p className="text-sm text-slate-500">Ningun producto de esta sucursal aparecera en busquedas del marketplace</p>
                        </div>
                      </label>
                    </div>

                    {/* Delivery Settings */}
                    {formData.delivery_enabled && (
                      <>
                        <div className="border-t border-slate-200 pt-6">
                          <h3 className="font-medium text-slate-900 mb-3">Configuración de entrega</h3>
                          
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Radio de cobertura (km)
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={formData.delivery_radius_km}
                                onChange={(e) => setFormData({ ...formData, delivery_radius_km: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Costo de envío fijo ($)
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={formData.delivery_fee}
                                onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                                placeholder="0 = Gratis"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Pedido mínimo ($)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={formData.min_order_amount}
                              onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                              placeholder="0 = Sin mínimo"
                            />
                          </div>
                        </div>

                        {/* Delivery Zones */}
                        <div className="border-t border-slate-200 pt-6">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="font-medium text-slate-900">Zonas de entrega (opcional)</h3>
                              <p className="text-sm text-slate-500">Define costos diferentes según la distancia</p>
                            </div>
                            <button
                              type="button"
                              onClick={addDeliveryZone}
                              className="text-sm text-teal-700 hover:text-teal-800 font-medium"
                            >
                              + Agregar zona
                            </button>
                          </div>
                          
                          {formData.delivery_zones.length > 0 ? (
                            <div className="space-y-2">
                              {formData.delivery_zones.map((zone, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.5"
                                      value={zone.min_km}
                                      onChange={(e) => updateDeliveryZone(idx, 'min_km', e.target.value)}
                                      className="w-16 px-2 py-1 border border-slate-200 rounded text-sm"
                                    />
                                    <span className="text-slate-400">-</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.5"
                                      value={zone.max_km}
                                      onChange={(e) => updateDeliveryZone(idx, 'max_km', e.target.value)}
                                      className="w-16 px-2 py-1 border border-slate-200 rounded text-sm"
                                    />
                                    <span className="text-sm text-slate-500">km</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-400">$</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={zone.fee}
                                      onChange={(e) => updateDeliveryZone(idx, 'fee', e.target.value)}
                                      className="w-20 px-2 py-1 border border-slate-200 rounded text-sm"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeDeliveryZone(idx)}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 italic">
                              Sin zonas configuradas. Se usará el costo fijo para todas las entregas.
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 p-6 border-t border-slate-200 bg-slate-50">
                <button
                  type="submit"
                  data-testid="branch-form-submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {editingBranch ? 'Guardar Cambios' : 'Crear Sucursal'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBranch(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Sucursales;
